import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthSessionContext } from "../routing/auth-session-store";
import { CheckCircle2, Heart, MapPin, Star } from "lucide-react";
import { useFetch } from "../../hooks/useFetch";
import { useFavoritesByClient, useUpdateFavorite } from "../../hooks/control";
import { getImageUri } from "../../core/utils/imageLoad";
import type { FavoriteResponse, ProviderResponse } from "../../core/types/control";

type ClientFavoritesProps = {
	clientIdProp?: number | null;
};

type ClientRelationLike = { id?: number | string } | Array<{ id?: number | string }>;

const parsePositiveId = (value: unknown): number | null => {
	if (typeof value === "number" && Number.isFinite(value) && value > 0) {
		return value;
	}

	if (typeof value === "string") {
		const parsed = Number(value);
		if (Number.isFinite(parsed) && parsed > 0) return parsed;
	}

	return null;
};

const extractClientId = (relation: ClientRelationLike | undefined): number | null => {
	if (!relation) return null;

	if (Array.isArray(relation)) {
		for (const client of relation) {
			const parsed = parsePositiveId(client?.id);
			if (parsed) return parsed;
		}
		return null;
	}

	return parsePositiveId(relation.id);
};

const toNonEmptyText = (value: unknown): string | null => {
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
};

type AddressLike = {
	name_city?: string | null;
	name_state?: string | null;
	street_address?: string | null;
	name_address?: string | null;
};

const buildLocationFromAddresses = (
	addresses?: Array<AddressLike | null | undefined> | null,
): string | null => {
	if (!Array.isArray(addresses)) return null;

	const firstAddressWithLocation = addresses.find((address) => {
		const city = toNonEmptyText(address?.name_city);
		const state = toNonEmptyText(address?.name_state);
		const street = toNonEmptyText(address?.street_address);
		const label = toNonEmptyText(address?.name_address);
		return Boolean(city || state || street || label);
	});

	if (!firstAddressWithLocation) return null;

	const city = toNonEmptyText(firstAddressWithLocation.name_city);
	const state = toNonEmptyText(firstAddressWithLocation.name_state);
	const administrativeParts = [city, state].filter(Boolean);
	if (administrativeParts.length > 0) {
		return administrativeParts.join(", ");
	}

	return (
		toNonEmptyText(firstAddressWithLocation.street_address) ??
		toNonEmptyText(firstAddressWithLocation.name_address)
	);
};

const getFavoriteLocationFromPayload = (favorite: FavoriteResponse): string | null => {
	const directAddress =
		toNonEmptyText(favorite.providers?.users?.addresses_user) ??
		toNonEmptyText(favorite.providers?.users?.adresses_user);
	if (directAddress) return directAddress;

	return buildLocationFromAddresses(favorite.providers?.users?.addresses);
};

export function ClientFavorites({ clientIdProp }: ClientFavoritesProps = {}) {
	const navigate = useNavigate();
	const { session } = useAuthSessionContext();
	const hasClientIdProp = typeof clientIdProp === "number" && clientIdProp > 0;
	const [clientId, setClientId] = useState<number | null>(clientIdProp ?? null);
	const [isResolvingClientId, setIsResolvingClientId] = useState(!hasClientIdProp);
	const [providerDetailsById, setProviderDetailsById] = useState<
		Record<number, ProviderResponse>
	>({});
	const pendingProviderLoadsRef = useRef<Set<number>>(new Set());
	const { execute: resolveUser } = useFetch<
		{ user?: { clients?: ClientRelationLike } } | { clients?: ClientRelationLike }
	>({
		endpoint: "/users/1",
		method: "GET",
		auto: false,
	});
	const { execute: fetchProviderById } = useFetch<ProviderResponse>({
		endpoint: "/providers/1",
		method: "GET",
		auto: false,
	});
	const resolveUserRef = useRef(resolveUser);
	const fetchProviderByIdRef = useRef(fetchProviderById);

	useEffect(() => {
		resolveUserRef.current = resolveUser;
	}, [resolveUser]);

	useEffect(() => {
		fetchProviderByIdRef.current = fetchProviderById;
	}, [fetchProviderById]);

	const {
		data: favoritesData,
		loading: isFavoritesLoading,
		execute: refreshFavorites,
	} = useFavoritesByClient(clientId ?? 0);
	const loading = isResolvingClientId || (clientId !== null && isFavoritesLoading);
	const didAutoRefreshRef = useRef(false);

	const favorites = useMemo(
		() =>
			Array.isArray(favoritesData)
				? favoritesData.filter((fav) => fav.is_favorite)
				: [],
		[favoritesData],
	);

	useEffect(() => {
		if (favorites.length === 0) return;

		const providerIdsToHydrate = favorites
			.map((favorite) => parsePositiveId(favorite.providers?.id))
			.filter(
				(providerId): providerId is number =>
					typeof providerId === "number" &&
					!providerDetailsById[providerId] &&
					!pendingProviderLoadsRef.current.has(providerId),
			);

		if (providerIdsToHydrate.length === 0) return;

		let cancelled = false;

		const hydrateMissingProviders = async () => {
			for (const providerId of providerIdsToHydrate) {
				if (cancelled) return;

				pendingProviderLoadsRef.current.add(providerId);
				try {
					const provider = await fetchProviderByIdRef.current({
						endpoint: `/providers/${providerId}`,
						method: "GET",
					});

					if (cancelled || !provider) continue;

					setProviderDetailsById((prev) => {
						if (prev[providerId]) return prev;
						return { ...prev, [providerId]: provider };
					});
				} catch {
					pendingProviderLoadsRef.current.delete(providerId);
				}
			}
		};

		void hydrateMissingProviders();

		return () => {
			cancelled = true;
		};
	}, [favorites, providerDetailsById]);

	const { toggle: toggleFavorite, loading: isUpdatingFavorite } = useUpdateFavorite(0);

	useEffect(() => {
		if (typeof clientIdProp === "number" && clientIdProp > 0) {
			setClientId(clientIdProp);
			setIsResolvingClientId(false);
			didAutoRefreshRef.current = false;
			return;
		}

		let cancelled = false;
		setIsResolvingClientId(true);

		const resolveClientId = async () => {
			if (!session?.user?.id) {
				if (!cancelled) {
					setClientId(null);
					setIsResolvingClientId(false);
				}
				return;
			}

			try {
				const payload = await resolveUserRef.current({
					endpoint: `/users/${session.user.id}`,
					method: "GET",
				});
				if (cancelled) return;

				const wrapped = payload as { user?: { clients?: ClientRelationLike } };
				const entity =
					wrapped?.user ?? (payload as { clients?: ClientRelationLike });
				setClientId(extractClientId(entity?.clients));
			} catch {
				if (!cancelled) setClientId(null);
			} finally {
				if (!cancelled) setIsResolvingClientId(false);
			}
		};

		void resolveClientId();

		return () => {
			cancelled = true;
		};
	}, [clientIdProp, session?.user?.id]);

	useEffect(() => {
		didAutoRefreshRef.current = false;
	}, [clientId]);

	useEffect(() => {
		if (!(clientId && clientId > 0) || !loading) {
			didAutoRefreshRef.current = false;
			return;
		}

		if (didAutoRefreshRef.current) return;

		const timeoutId = window.setTimeout(() => {
			didAutoRefreshRef.current = true;
			void refreshFavorites({});
		}, 8000);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [clientId, loading, refreshFavorites]);

	const handleRemove = async (favorite: FavoriteResponse) => {
		try {
			await toggleFavorite(false, favorite.id);
			await refreshFavorites({});
		} catch {
			// Keep current list if backend update fails.
		}
	};

	const getProviderName = (favorite: FavoriteResponse) => {
		const company = favorite.providers?.company_name?.trim();
		if (company) return company;

		const fullName = [
			favorite.providers?.users?.names,
			favorite.providers?.users?.last_names,
		]
			.filter((value) => typeof value === "string" && value.trim().length > 0)
			.join(" ")
			.trim();

		return fullName || "Proveedor";
	};

	const getProviderCategory = (favorite: FavoriteResponse) => {
		const category =
			favorite.providers?.categories_providers?.[0]?.categories_services
				?.name_category;
		return category?.trim() || "Sin categoría";
	};

	const getProviderLocation = (favorite: FavoriteResponse) => {
		const directLocation = getFavoriteLocationFromPayload(favorite);
		if (directLocation) return directLocation;

		const providerId = parsePositiveId(favorite.providers?.id);
		const providerDetails =
			typeof providerId === "number" ? providerDetailsById[providerId] : undefined;

		const hydratedLocation =
			buildLocationFromAddresses(providerDetails?.users?.addresses) ??
			toNonEmptyText(providerDetails?.users?.addresses_user);

		return hydratedLocation ?? "Ubicación no disponible";
	};

	const getProviderRating = (favorite: FavoriteResponse) => {
		const rating = Number(favorite.providers?.rating);
		if (!Number.isFinite(rating) || rating <= 0) return "0.0";
		return rating.toFixed(1);
	};

	const getProviderReviews = (favorite: FavoriteResponse) => {
		const providerId = parsePositiveId(favorite.providers?.id);
		const providerDetails =
			typeof providerId === "number" ? providerDetailsById[providerId] : undefined;
		const providerReviews = Array.isArray(favorite.providers?.reviews_qualified)
			? favorite.providers.reviews_qualified.length
			: null;
		const userReviews = Array.isArray(favorite.providers?.users?.reviews_qualified)
			? favorite.providers.users.reviews_qualified.length
			: null;
		const hydratedReviews = Array.isArray(providerDetails?.users?.reviews_qualified)
			? providerDetails.users.reviews_qualified.length
			: null;
		const reviews =
			typeof favorite.providers?.reviews_count === "number"
				? favorite.providers.reviews_count
				: typeof providerReviews === "number"
					? providerReviews
					: typeof userReviews === "number"
						? userReviews
						: hydratedReviews;
		if (typeof reviews === "number" && Number.isFinite(reviews) && reviews >= 0) {
			return `(${reviews} reseñas)`;
		}
		return "(sin reseñas)";
	};

	const getProviderAvatar = (favorite: FavoriteResponse) => {
		return getImageUri(favorite.providers?.users?.image_profile);
	};

	const handleHire = (favorite: FavoriteResponse) => {
		const providerId = favorite.providers?.id;
		if (!providerId) return;

		navigate(`/proveedor/${providerId}`, {
			state: {
				provider: { id: providerId },
				openHireModal: true,
			},
		});
	};

	return (
		<div className='space-y-4 px-1 py-2 sm:px-2 sm:py-3'>
			<h2 className='text-base font-semibold text-slate-800 sm:text-lg'>
				Proveedores Favoritos
			</h2>
			{loading ? (
				<p className='text-sm text-slate-500'>Cargando favoritos...</p>
			) : clientId === null ? (
				<p className='text-sm text-slate-500'>
					No se pudo identificar el perfil de cliente.
				</p>
			) : favorites.length === 0 ? (
				<p className='text-sm text-slate-500'>No tienes proveedores favoritos.</p>
			) : (
				<div className='grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3'>
					{favorites.map((fav) => (
						<article
							key={fav.id}
							className='flex h-full flex-col rounded-2xl border border-slate-200 bg-[#f1f3f5] p-3.5 shadow-sm sm:p-4'
						>
							<div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
								<div className='flex min-w-0 items-start gap-3'>
									<div className='h-12 w-12 shrink-0 overflow-hidden rounded-full border border-slate-300 bg-slate-200 sm:h-14 sm:w-14'>
										{getProviderAvatar(fav) ? (
											<img
												src={getProviderAvatar(fav)}
												alt={getProviderName(fav)}
												className='h-full w-full object-cover'
											/>
										) : (
											<div className='flex h-full w-full items-center justify-center text-sm font-semibold text-slate-600 sm:text-base'>
												{getProviderName(fav)
													.charAt(0)
													.toUpperCase()}
											</div>
										)}
									</div>

									<div className='min-w-0 pt-0.5'>
										<h3 className='truncate text-base font-semibold leading-tight text-[#0e8bb8] sm:text-lg'>
											{getProviderName(fav)}
										</h3>
										<p className='mt-0.5 text-xs font-medium leading-snug text-slate-600 sm:text-sm sm:truncate'>
											Categoria: {getProviderCategory(fav)}
										</p>
									</div>
								</div>

								<div className='flex shrink-0 items-center justify-end gap-1.5 sm:justify-start'>
									<CheckCircle2 className='h-4 w-4 text-[#0e8bb8] sm:h-5 sm:w-5' />
									<button
										type='button'
										className={`rounded-full p-1 transition hover:bg-slate-200 disabled:cursor-not-allowed cursor-pointer disabled:opacity-60 ${fav.is_favorite ? "text-rose-500" : "text-slate-400 hover:text-rose-500"}`}
										title={
											fav.is_favorite
												? "Quitar de favoritos"
												: "Agregar a favoritos"
										}
										disabled={isUpdatingFavorite}
										onClick={() => void handleRemove(fav)}
									>
										<Heart
											className={`h-4 w-4 sm:h-5 sm:w-5 ${fav.is_favorite ? "fill-rose-500" : "fill-none"}`}
										/>
									</button>
								</div>
							</div>

							<div className='mt-3 flex flex-col gap-2 text-slate-400 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1.5'>
								<div className='flex items-center gap-1.5 sm:gap-2'>
									<Star className='h-4 w-4 fill-[#9a6400] text-[#9a6400] sm:h-5 sm:w-5' />
									<span className='text-sm font-semibold text-slate-800 sm:text-base'>
										{getProviderRating(fav)}
									</span>
									<span className='text-[11px] font-medium sm:text-xs'>
										{getProviderReviews(fav)}
									</span>
								</div>

								<div className='flex min-w-0 items-start gap-1.5 sm:items-center sm:gap-2'>
									<MapPin className='h-3.5 w-3.5 shrink-0 text-slate-400 sm:h-4 sm:w-4' />
									<span className='text-[11px] font-medium leading-snug sm:truncate sm:text-xs'>
										{getProviderLocation(fav)}
									</span>
								</div>
							</div>

							<button
								type='button'
								onClick={() => handleHire(fav)}
								disabled={!fav.providers?.id}
								className='mt-3 w-full cursor-pointer rounded-xl bg-[#bfe2ef] px-3 py-2 text-sm font-medium text-[#0d89b6] transition hover:bg-[#afd7e7] disabled:cursor-not-allowed disabled:opacity-70'
							>
								Contratar
							</button>
						</article>
					))}
				</div>
			)}
		</div>
	);
}
